import React, { Dispatch, useEffect, useRef } from "react";
import { has, omit } from "lodash";
import moment, { Moment } from "moment";
import { FormattedMessage, IntlShape } from "react-intl";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Field, reduxForm, InjectedFormProps, Fields, formValueSelector } from "redux-form";
import { Action } from "typescript-fsa";

import { ApiAction } from "@api/actions";
import { AsyncForm } from "@api/components/AsyncForm";
import { connectToApi } from "@api/components/connectToApi";
import { FormModelSelect } from "@api/components/FormModelSelect";
import { Api } from "@api/index";
import { ApiRequest } from "@api/models";
import { withLocale, InjectedLocaleProps } from "@localisation/components/withLocale";
import { AlbumSelect } from "@music/components/AlbumSelect";
import { TrackSelect } from "@music/components/TrackSelect";
import { Album, Track } from "@music/models";
import { MAX_FILTER_DAYS } from "@sales/constants";
import { FormDateRange } from "@app/modules/ui/Form/FormDateRange";
import { Alert } from "@ui/Alert";
import { FormInputHidden } from "@ui/Form/FormInputHidden";
import { FormSelect } from "@ui/Form/FormSelect";
import { messages as uiMessages } from "@ui/messages";
import { testRulesOnValue, validate, ValidationRules } from "@validation/index";
import { IReport, ReportType, ReportsState } from "../../models";
import { getCustomReportTypes, getRequestIdCustomReports, hasReportInProgress } from "../../selectors";
import { messages } from "../../messages";

interface ReportFormStateProps {
    types: ReportType[];
    requestId: string;
    isReportInProgress: boolean;
    reportTypeId: number;
}

interface ReportFormOwnProps {
    onSuccess?: () => void;
    intl: IntlShape;
}

interface FormData {
    startDate: Moment;
    endDate: Moment;
    reportTypeId: number;
    fname: string;
    description: string;
    [key: string]: any;
}

interface ReportFormDispatchProps {
    reportsStore: (data: FormData) => Action<ApiRequest>;
}

type Props = ReportFormOwnProps & ReportFormStateProps & ReportFormDispatchProps
    & InjectedLocaleProps & InjectedFormProps;

// The validation rules.
const rules: ValidationRules = {
    startDate: {
        label: messages.reportColumnPeriod,
        rules: [
            { type: "required" },
        ],
    },
    endDate: {
        label: messages.reportColumnPeriod,
        rules: [
            { type: "required" },
        ],
    },
    reportTypeId: {
        label: messages.reportColumnType,
        rules: [
            { type: "required" },
        ],
    },
};

const formatMoment = (m: Moment) => m == null ? null : m.format("YYYY-MM-DD");
const getFname = (startDate: Moment, endDate: Moment, description: string) => {
    return description.replace(/[^a-z0-9]/gi, "-").replace(/\-+/g, "-").toLowerCase() + "-"
        + (startDate == null ? "" : startDate.format("YYYYMMDD") + "-")
        + (endDate == null ? "" : endDate.format("YYYYMMDD") + "_")
        + "*.csv";
};

const getReportTypeForId = (types: ReportType[], id: number) => types.find((t) => t.id === id);

const validateRequired = (
    value: any,
    _allValues: FormData,
    { locale, types, reportTypeId, intl: { formatMessage } }: Props,
    name: string,
) => {
    const type = getReportTypeForId(types, reportTypeId);
    const label = type?.config?.constraints.find((c) => c.name === name)?.label[locale];
    return testRulesOnValue(
        value,
        label,
        [{type: "required"}],
        formatMessage,
    );
};

const FormAlbumSelect = FormModelSelect<Album>;
const FormTrackSelect = FormModelSelect<Track>;

const ReportForm = ({
    onSuccess,
    types,
    requestId,
    isReportInProgress,
    reportTypeId,
    reportsStore,
    locale,
    handleSubmit,
    change,
    error,
    reset,
    intl,
}: Props) => {
    const { formatMessage } = intl;

    const earliestDate = moment("2023-01-01");
    const latestDate = moment().subtract(2, "month").endOf("month");

    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        if (onSuccess != null) {
            reset();
            onSuccess();
        }
        firstUpdate.current = true;
    }, [requestId]);

    const type = getReportTypeForId(types, reportTypeId);

    return (
        <div className="report-form">
            {isReportInProgress &&
                <Alert style="error">
                    <FormattedMessage {...messages.customReportsInProgressError} />
                </Alert>
            }
            {!isReportInProgress &&
                <AsyncForm
                    submitText={formatMessage(uiMessages.buttonCreate)}
                    actionCreator={reportsStore as any}
                    handleSubmit={handleSubmit}
                    error={error}
                >
                    <Field
                        name="fname"
                        type="hidden"
                        component={FormInputHidden}
                    />
                    <Field
                        name="description"
                        type="hidden"
                        component={FormInputHidden}
                    />
                    <Fields
                        names={["startDate", "endDate"]}
                        component={FormDateRange}
                        // FormDateRange props
                        startDateName="startDate"
                        endDateName="endDate"
                        startDateId="report-form-from"
                        endDateId="report-form-to"
                        startDatePlaceholderText={formatMessage(uiMessages.datesStart)}
                        endDatePlaceholderText={formatMessage(uiMessages.datesEnd)}
                        earliestDate={earliestDate}
                        latestDate={latestDate}
                        maxDays={MAX_FILTER_DAYS}
                        label={formatMessage(messages.reportColumnPeriod)}
                        showLabel={true}
                    />
                    <Field
                        name="reportTypeId"
                        type="select"
                        component={FormSelect as any}
                        options={types}
                        valueKey="id"
                        labelKey="label"
                        locale={locale}
                        showLabel={true}
                        multiple={false}
                        id="report-form-report-type-select"
                        label={formatMessage(messages.reportColumnType)}
                    />
                    {/* Dynamic fields */}
                    {type != null && type.config?.constraints?.map((constraint) => {
                        const label = constraint.label[locale];
                        const props: any = {
                            key: constraint.name,
                            name: constraint.name,
                            label,
                            showLabel: true,
                            locale,
                            multiple: false,
                        };
                        if (constraint.type === "album") {
                            props.component = FormAlbumSelect as any;
                            props.componentClass = AlbumSelect;
                            props.multiple = true;
                            props.getOptionValue = ({ id }: Album) => id;
                            props.onModelChange = (album: Album | Album[]) => {
                                const albums = Array.isArray(album) ? album : [album];
                                const titles = albums.map((a) => a.artistsStr + " - " + a.title);
                                const desc = titles.join(", ");
                                change("description", desc);
                            };
                            props.id = "report-form-album-select";
                        }
                        else if (constraint.type === "track") {
                            props.component = FormTrackSelect as any;
                            props.componentClass = TrackSelect;
                            props.multiple = true;
                            props.getOptionValue = ({ id }: Track) => id;
                            props.onModelChange = (track: Track | Track[]) => {
                                const tracks = Array.isArray(track) ? track : [track];
                                const titles = tracks.map((t) => t.artistsStr + " - " + t.title);
                                const desc = titles.join(", ");
                                change("description", desc);
                            };
                            props.id = "report-form-track-select";
                        }

                        return (
                            <Field {...props} validate={validateRequired} />
                        );
                    })}
                </AsyncForm>
            }
        </div>
    );
};

const makeMapStateToProps = () => {
    const formSelector = formValueSelector("report-form");
    return (state: ReportsState): ReportFormStateProps => {
        let reportTypeId = formSelector(state, "reportTypeId");
        if (has(reportTypeId, "value")) {
            reportTypeId = reportTypeId.value;
        }
        return {
            types: getCustomReportTypes(state),
            requestId: getRequestIdCustomReports(state),
            isReportInProgress: hasReportInProgress(state),
            reportTypeId,
        };
    };
};

const mapDispatchToProps = (dispatch: Dispatch<ApiAction>): ReportFormDispatchProps => ({
    reportsStore: (data: FormData) => {
        const otherData = omit(data, "startDate", "endDate", "reportTypeId", "fname", "description");
        const vars = Object.keys(otherData).reduce<{ [key: string]: any }>((acc, key) => {
            acc["$" + key] = otherData[key] as any;
            return acc;
        }, {
            $fname: getFname(data.startDate, data.endDate, data.description),
        });
        const report = {
            description: data.description,
            statusName: "not-processed",
            startDate: formatMoment(data.startDate),
            endDate: formatMoment(data.endDate),
            reportedDate: formatMoment(data.endDate),
            reportTypeId: data.reportTypeId,
            config: { vars },
        };
        const action = Api.reportsStore(report as unknown as IReport);
        dispatch(action);
        return action;
    },
});

const ConnectedReportForm = compose<Props, ReportFormOwnProps>(
    connect(makeMapStateToProps, mapDispatchToProps),
    connectToApi({
        actionCreators: [Api.reportTypesIndex],
        showRefresh: false,
    }),
    reduxForm({
        form: "report-form",
        validate: validate(rules),
        destroyOnUnmount: false,
    }),
    withLocale,
)(ReportForm);

export {
    ConnectedReportForm as ReportForm,
    ReportForm as ReportFormPresenter,
    Props as ReportFormProps,
    FormData as ReportFormData,
};
